import { useEffect, useState } from "react";
import { ArrowUpRight, Calendar, Clock } from "lucide-react";
import { Reveal } from "../components/Motion";
import { fetchInsights } from "../lib/api";
import CTASection from "../components/CTASection";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInsights()
      .then((data) => setPosts(data))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  const formatDate = (iso) =>
    new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });

  return (
    <main data-testid="blog-page" className="pt-32">
      <section className="px-6 md:px-8 pb-16">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <span className="overline">Insights</span>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-medium leading-[0.95] tracking-tight mt-6 max-w-5xl">
              Field notes from{" "}
              <em className="text-[#D4AF37] not-italic">the practice</em>.
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-2xl text-white/60 text-lg leading-relaxed">
              Frameworks, audits, and operator-level thinking — published when
              we have something worth saying.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="px-6 md:px-8 py-16">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="text-white/40 text-sm" data-testid="blog-loading">
              Loading insights...
            </div>
          ) : posts.length === 0 ? (
            <div className="text-white/40 text-sm" data-testid="blog-empty">
              No insights available yet.
            </div>
          ) : (
            <>
              {/* Featured (first post) */}
              <Reveal>
                <a
                  href="#"
                  data-testid={`blog-featured-${posts[0].id}`}
                  className="block group border border-white/5 bg-[#0E0E0E] mb-12 overflow-hidden"
                >
                  <div className="grid grid-cols-1 lg:grid-cols-12">
                    <div className="lg:col-span-7 relative h-[300px] lg:h-[460px] overflow-hidden">
                      <img
                        src={posts[0].image}
                        alt={posts[0].title}
                        className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0B]/70 via-transparent to-transparent" />
                    </div>
                    <div className="lg:col-span-5 p-10 lg:p-14 flex flex-col justify-between">
                      <div>
                        <div className="flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-6">
                          <span>{posts[0].category}</span>
                          <span className="w-1 h-1 bg-white/30 rounded-full" />
                          <span>Featured</span>
                        </div>
                        <h2 className="font-display text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight">
                          {posts[0].title}
                        </h2>
                        <p className="text-white/55 mt-6 leading-relaxed">
                          {posts[0].excerpt}
                        </p>
                      </div>
                      <div className="mt-10 flex items-center justify-between">
                        <div className="flex items-center gap-5 text-xs text-white/40">
                          <div className="flex items-center gap-2">
                            <Calendar size={12} /> {formatDate(posts[0].date)}
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock size={12} /> {posts[0].read_time}
                          </div>
                        </div>
                        <ArrowUpRight
                          size={20}
                          className="text-[#D4AF37] group-hover:rotate-45 transition-transform duration-500"
                        />
                      </div>
                    </div>
                  </div>
                </a>
              </Reveal>

              {/* Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 border border-white/5">
                {posts.slice(1).map((post, i) => (
                  <Reveal key={post.id} delay={i * 0.05}>
                    <a
                      href="#"
                      data-testid={`blog-card-${post.id}`}
                      className="block bg-[#0B0B0B] hover:bg-[#141414] transition-colors group h-full"
                    >
                      <div className="relative h-56 overflow-hidden">
                        <img
                          src={post.image}
                          alt={post.title}
                          className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                        />
                      </div>
                      <div className="p-8">
                        <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-4">
                          {post.category}
                        </div>
                        <h3 className="font-display text-xl md:text-2xl leading-snug group-hover:text-[#D4AF37] transition-colors">
                          {post.title}
                        </h3>
                        <p className="text-white/50 text-sm mt-3 leading-relaxed line-clamp-2">
                          {post.excerpt}
                        </p>
                        <div className="mt-6 flex items-center justify-between text-xs text-white/40 pt-6 border-t border-white/5">
                          <span>{formatDate(post.date)}</span>
                          <span>{post.read_time}</span>
                        </div>
                      </div>
                    </a>
                  </Reveal>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <CTASection testId="blog-cta" />
    </main>
  );
}
